import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";
import { FaEdit, FaTimes } from "react-icons/fa";
import "./users_style.css";
import "@github/relative-time-element";
import ScrollToTop from "../../../../components/scrollToTop/scrollToTop";
import { IoMdInformationCircleOutline } from "react-icons/io";
import DeletedUsers from "./DeletedUsers";
import TableControls from "../../../../components/tableControler/tableControler";
import UpdateUserComponent from "./UpdateUserComponent";

function Users({ handleMenuClick }) {
  const [users, setUsers] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const [userProfiles, setUserProfiles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);

  const [showUpdate, setShowUpdate] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [showUsers, setShowUsers] = useState(false);

  const handleUpdateClick = (id) => {
    setSelectedUserId(id);
    setShowUpdate(true);
  };

  const handleCloseUpdate = () => {
    setShowUpdate(false);
    setSelectedUserId(null);
  };

  const handleUpdateSuccess = () => {
    setUpdateTrigger(!updateTrigger); // Refresca la lista de usuarios
    handleCloseUpdate();
  };

  const filteredData = searchValue
    ? users.filter((data) =>
        (data.first_name || "")
          .toUpperCase()
          .includes(searchValue.toUpperCase())
      )
    : users;

  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;

  const currentData = filteredData.slice(indexOfFirstData, indexOfLastData);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPageCount = Math.ceil(filteredData.length / dataPerPage);
  const pageNumbers = [];
  const maxPageNumbersToShow = 20;

  let startPage, endPage;
  if (totalPageCount <= maxPageNumbersToShow) {
    startPage = 1;
    endPage = totalPageCount;
  } else {
    startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
    endPage = Math.min(totalPageCount, startPage + maxPageNumbersToShow - 1);

    if (endPage - startPage < maxPageNumbersToShow) {
      startPage = endPage - maxPageNumbersToShow + 1;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const showEntries = (e) => {
    setDataPerPage(e.target.value);
  };

  const handleSearch = (value) => {
    setSearchValue(value.trim());
  };

  const getRoleId = (roleId) => {
    switch (roleId) {
      case 1:
        return "Administrador";
      case 2:
        return "Super Admin";
      case 3:
        return "Usuario";
      default:
        return "Desconocido";
    }
  };

  const getAreaId = (areaId) => {
    const area = areas.find((a) => a.id === areaId);
    return area ? area.name : "-";
  };

  const getAreas = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/users/getAreas`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      setAreas(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getRoles = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/users/getRoles`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      setRoles(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  //Obtener perfiles de usuario para el select de user_type
  const getUserProfiles = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/user_profiles`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      setUserProfiles(res.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getUserProfiles();
    getAreas();
    getRoles();
  }, []);

  //Obtener datos de los usuarios
  const getUsers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/users`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      if (res.status === 200) {
        setUsers(res.data.data);
        setLoading(false);
      } else {
        Swal.fire("Error", "Hubo un error al obtener los datos", "error");
        setLoading(false);
      }
    } catch (error) {
      Swal.fire("Error", "Hubo un error al obtener los datos", "error");
      setLoading(false);
    }
  };

  //Eliminar usuario
  const deleteUser = async (id) => {
    try {
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¡No podrás revertir esto!",
        icon: "warning",
        showDenyButton: true,
        confirmButtonText: "Sí, eliminarlo",
        denyButtonText: "No, cancelar",
        reverseButtons: true,
        buttonsStyling: true,
      });

      if (result.isConfirmed) {
        const response = await axios.delete(
          `${process.env.REACT_APP_URL_BACKEND}/api/v1/users/delete/${id}`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );
        console.log("Response:", response);

        Swal.fire("¡Eliminado!", "El elemento ha sido eliminado.", "success");
        setUpdateTrigger(!updateTrigger);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        Swal.fire("Cancelado");
      }
    } catch (error) {
      Swal.fire("Error", "Hubo un error", "error");
    }
  };

  //Mostrar informacion del usuario
  const showUser = async (id) => {
    try {
      //Obtencion de datos del usuario mediante una solicitud get
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/users/${id}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      const userData = response.data.data;

      await Swal.fire({
        title: "Ver usuario",
        html: `<div class="user-details-table" style="line-height: 1.6em; text-align: left; margin: 0 auto;">
                        <table style="width: 100%; border-collapse: collapse;">
                            <tbody>
                                <tr>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Nombre:</strong></td>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">${
                                      userData[0].first_name
                                    }</td>
                                </tr>
                                <tr>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Apellido:</strong></td>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">${
                                      userData[0].last_name
                                    }</td>
                                </tr>
                                <tr>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Correo Electrónico:</strong></td>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">${
                                      userData[0].email
                                    }</td>
                                </tr>
                                <tr>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Permisos:</strong></td>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">${getRoleId(
                                      userData[0].role_id
                                    )}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Área:</strong></td>
                                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">${getAreaId(
                                      userData[0].area_id
                                    )}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>`,
        width: 700,
        padding: "1em",
      });

      // No es necesario manejar resultado ya que no hay botones para confirmar o cancelar
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Hubo un error", "error");
    }
  };

  //Crear usuarios
  const createUser = async () => {
    try {
      const reactComponentHtml = ReactDOMServer.renderToString(
        <>
          <form
            acceptCharset="UTF-8"
            id="formValidation"
            className="form-horizontal fv-form fv-form-bootstrap"
            autoComplete="off"
            encType="multipart/form-data"
            noValidate="novalidate"
          >
            <div className="row mb-3 text-start">
              <div className="col-md-6 mb-3">
                <label htmlFor="first_name">Nombre *</label>
                <input
                  type="text"
                  className="form-control"
                  id="first_name"
                  placeholder="Nombre"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="last_name">Apellido *</label>
                <input
                  type="text"
                  className="form-control"
                  id="last_name"
                  placeholder="Número de empleado"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="email">Correo Electrónico *</label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="Correo"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="role_id">Permisos *</label>
                <select className="form-select" id="role_id" defaultValue="">
                  {roles.map((rol) => (
                    <option key={rol.id} value={rol.id}>
                      {rol.name}
                    </option>
                  ))}
                </select>
                {/* <select className="form-select" id="role_id"  defaultValue="3">
                                <option value="1">Administrador</option>
                                <option value="2">Super Admin</option>
                                <option value="3">Usuario</option>
                            </select> */}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="area_id">Área *</label>
                <select className="form-select" id="area_id" defaultValue="">
                  <option value="">Elija una opción</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="user_type">Perfil de usuarios *</label>
                <select className="form-select" id="user_type">
                  <option value="">Elija una opción</option>
                  {userProfiles.map((userProfile) => (
                    <option key={userProfile.id} value={userProfile.id}>
                      {userProfile.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="password">Contraseña *</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  placeholder="Contraseña"
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="confirm_password">Confirmar Contraseña *</label>
                <input
                  type="password"
                  className="form-control"
                  id="confirm_password"
                  placeholder="Confirmar Contraseña"
                  required
                />
              </div>
            </div>
          </form>
        </>
      );

      const result = await Swal.fire({
        title: "Crear Usuario",
        html: reactComponentHtml,
        showDenyButton: true,
        confirmButtonText: "Crear",
        denyButtonText: "Cancelar",
        width: 1300,
        padding: "1em",
        color: "#000",
        backdrop: `
                    rgba(0,0,0,0.8)
                `,
        customClass: {
          confirmButton: "btn-amc-blue",
          denyButton: "btn-cancel",
          title: "alert-title",
          popup: "alert-bg",
        },
        preConfirm: async () => {
          const first_name = document.getElementById("first_name").value;
          const last_name = document.getElementById("last_name").value;
          const email = document.getElementById("email").value;
          const role_id = document.getElementById("role_id").value;
          const area_id = document.getElementById("area_id").value;
          const user_profile = document.getElementById("user_type").value;
          const password = document.getElementById("password").value;
          const confirm_password =
            document.getElementById("confirm_password").value;

          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          if (!first_name || !last_name) {
            Swal.showValidationMessage("El nombre y apellido son obligatorios");
            return false;
          } else if (!email) {
            Swal.showValidationMessage("El correo es obligatorio");
            return false;
          } else if (!emailRegex.test(email)) {
            Swal.showValidationMessage(
              "Por favor, ingrese un correo electrónico válido"
            );
            return false;
          } else if (!role_id) {
            Swal.showValidationMessage(
              "Por favor, elija los permisos del usuario"
            );
            return false;
          } else if (!area_id) {
            Swal.showValidationMessage("Por favor, elija una area");
            return false;
          } else if (!user_profile) {
            Swal.showValidationMessage("Por favor, elija un perfil de usuario");
            return false;
          } else if (!password) {
            Swal.showValidationMessage("Ingrese una contraseña");
            return false;
          }
          if (password !== confirm_password) {
            Swal.showValidationMessage(
              "La contraseña y la confirmación de contraseña deben coincidir"
            );
            return false;
          }

          return {
            first_name,
            last_name,
            email,
            role_id,
            area_id,
            user_profile,
            password,
          };
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });

      if (result.isConfirmed) {
        const userData = result.value;

        const response = await axios.post(
          `${process.env.REACT_APP_URL_BACKEND}/api/v1/users/create`,
          userData,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );

        Swal.fire(
          "¡Creado!",
          "El usuario ha sido creado exitosamente.",
          "success"
        );
        setUpdateTrigger(!updateTrigger);
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        Swal.fire("Cancelado", "Acción cancelada.", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Hubo un error", "error");
    }
  };

  useEffect(() => {
    getUsers();
  }, [updateTrigger]);

  return (
    <>
      <Helmet>
        <title>Horus | Usuarios</title>
      </Helmet>
      <ScrollToTop />
      {!showUpdate && (
        <div className="table-responsive">
          <div
            className="dataTables_wrapper dt-bootstrap4 no-footer"
            id="datatables_wrapper"
          >
            <div className="text-right mb-2 ">
              <button
                className="btnn"
                onClick={() => setShowDeleted(!showDeleted)}
              >
                {showDeleted ? "Ver Activos" : "Ver Eliminados"}
              </button>
            </div>
            {showDeleted ? (
              <DeletedUsers
                setUpdateTrigger={setUpdateTrigger}
                updateTrigger={updateTrigger}
              />
            ) : (
              <>
                <div className="text-right mb-4 ">
                  <button className="btnn" onClick={createUser}>
                    Añadir Usuario
                  </button>
                </div>
                <TableControls
                  onSearch={handleSearch}
                  onEntriesChange={showEntries}
                  liveSearch={true}
                  debounceTime={500}
                />

                <div className="row">
                  <div className="col-sm-12">
                    <table
                      className="table table-inverse table-hover table-striped table-bordered dataTable no-footer"
                      role="grid"
                      aria-describedby="datatables_info"
                    >
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Nombre</th>
                          <th>Apellido</th>
                          <th>Correo Electrónico</th>
                          <th>Permisos</th>
                          <th>Área</th>
                          <th>Creado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentData.map((user) => (
                          <tr key={user.id} data-master-id={user.id}>
                            <td>{user.id}</td>
                            <td>{user.first_name}</td>
                            <td>{user.last_name}</td>
                            <td>{user.email}</td>
                            <td>{getRoleId(user.role_id)}</td>
                            <td>{getAreaId(user.area_id)}</td>
                            <td>
                              <relative-time
                                datetime={user.created_at}
                                lang="es"
                              />
                            </td>
                            <td>
                              <IoMdInformationCircleOutline
                                className="detailsButton"
                                onClick={() => {
                                  showUser(user.id);
                                }}
                              />
                              <FaEdit
                                className="editButton"
                                onClick={() => handleUpdateClick(user.id)}
                              />
                              <FaTimes
                                className="deleteButton"
                                onClick={() => {
                                  deleteUser(user.id);
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="row">
                  <nav aria-label="Page navigation example">
                    <ul className="pagination">
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </button>
                      </li>
                      {pageNumbers.map((pageNumber) => (
                        <li className="page-item" key={pageNumber}>
                          <button
                            className="page-link"
                            onClick={() => paginate(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        </li>
                      ))}
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === pageNumbers.length}
                        >
                          Siguiente
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showUpdate && (
        <UpdateUserComponent
          userId={selectedUserId}
          onClose={handleCloseUpdate}
          onUpdate={handleUpdateSuccess}
        />
      )}
    </>
  );
}

export default Users;
